import { useCallback } from "react";
import { useOutletContext } from "react-router";
import styled from "styled-components";

import AddressFieldSet from "./AddressFieldSet";
import useBrandWriter from "./useBrandWriter";
import useEditor from "./useEditor";
import * as UI from "../../../../modules/ui-kit";
import {
    useUnsavedChangesEditor,
    type UnsavedChangesContext,
} from "../../../../modules/unsaved-changes";
import Brand from "../../domain/Brand";

const ErrorMessage = styled.p`
    color: #c00;
    font-size: 0.9rem;
    margin: 0;
`;

const InfoMessage = styled.p`
    color: #333;
    font-size: 0.9rem;
    margin: 0;
`;

export default function BrandEditor(props) {
    const { brand, onSave } = props;

    const guard = useOutletContext<UnsavedChangesContext>();

    const { reader, reset, writer: rawWriter } = useEditor<Brand>(brand);
    const { save, error, info } = useBrandWriter(reader, onSave);

    const writer = useCallback(
        (key: string) => (value: unknown) => {
            if (guard && JSON.stringify(reader?.[key]) !== JSON.stringify(value)) {
                guard.isDirtyRef.current = true;
            }
            rawWriter(key)(value);
        },
        [rawWriter, guard, reader],
    );

    const guardedSave = useCallback(
        async (event?: { preventDefault?: () => void }) => {
            const ok = await save(event);
            if (ok && guard) guard.isDirtyRef.current = false;
            return ok;
        },
        [save, guard],
    );

    const guardedReset = useCallback(() => {
        reset();
        if (guard) guard.isDirtyRef.current = false;
    }, [reset, guard]);

    useUnsavedChangesEditor(guard, guardedSave);

    return (
        <UI.Form
            onReset={ guardedReset }
            onSubmit={ guardedSave }
        >
            <UI.Field>
                <UI.FieldLabel>ID</UI.FieldLabel>
                <span>{ reader.id }</span>
            </UI.Field>
            <UI.Field>
                <UI.FieldLabel>legacy ID</UI.FieldLabel>
                <UI.TextInput
                    onChange={ writer("legacyID") }
                    placeholder="Legacy ID"
                    value={ reader.legacyID ?? "" }
                />
            </UI.Field>
            <UI.Field>
                <UI.FieldLabel>legacy Alias</UI.FieldLabel>
                <UI.TextInput
                    onChange={ writer("legacyAlias") }
                    placeholder="Legacy alias"
                    value={ reader.legacyAlias ?? "" }
                />
            </UI.Field>
            <UI.Field>
                <UI.FieldLabel>Name</UI.FieldLabel>
                <UI.TextInput
                    onChange={ writer("name") }
                    placeholder="Brand name"
                    value={ reader.name ?? "" }
                />
            </UI.Field>
            <UI.Field>
                <UI.FieldLabel>Logo</UI.FieldLabel>
                <UI.TextInput
                    onChange={ writer("logo") }
                    placeholder="Brand logo url"
                    value={ reader.logo ?? "" }
                />
            </UI.Field>
            <UI.Field>
                <UI.FieldLabel>Color</UI.FieldLabel>
                <UI.TextInput
                    onChange={ writer("color") }
                    placeholder="#ffdd00"
                    value={ reader.color ?? "" }
                />
            </UI.Field>
            <UI.Field>
                <UI.FieldLabel>About</UI.FieldLabel>
                <UI.TranslatedInput
                    label="about"
                    onChange={ writer("about") }
                    value={ reader.about ?? [] }
                />
            </UI.Field>
            <AddressFieldSet
                onChange={ writer("address") }
                value={ reader.address }
            />
            <UI.Field>
                <UI.FieldLabel>Cover image</UI.FieldLabel>
                <UI.TextInput
                    onChange={ writer("coverImage") }
                    placeholder="Brand cover image url"
                    value={ reader.coverImage ?? "" }
                />
            </UI.Field>
            <UI.Field>
                <UI.FieldLabel>URL</UI.FieldLabel>
                <UI.TextInput
                    onChange={ writer("url") }
                    placeholder="Brand url"
                    value={ reader.url ?? "" }
                />
            </UI.Field>
            <UI.Field>
                <UI.FieldLabel>Alternative names</UI.FieldLabel>
                <UI.ListInput
                    onChange={ writer("alternativeNames") }
                    value={ reader.alternativeNames ?? [] }
                />
            </UI.Field>
            <UI.Field>
                <UI.FieldLabel>Features</UI.FieldLabel>
                <UI.ListInput
                    onChange={ writer("features") }
                    value={ reader.features ?? [] }
                />
            </UI.Field>
            <UI.Field>
                <UI.FieldLabel>Primary language</UI.FieldLabel>
                <UI.TextInput
                    onChange={ writer("primaryLanguage") }
                    placeholder="Primary language"
                    value={ reader.primaryLanguage ?? "" }
                />
            </UI.Field>
            <UI.Field>
                <UI.FieldLabel>Enable catalog</UI.FieldLabel>
                <UI.SegmentedControl
                    disabled={ !save }
                    onChange={ writer("enableCatalog") }
                    options={
                        [
                            { label: "Yes", value: true },
                            { label: "No", value: false },
                        ]
                    }
                    value={ reader.enableCatalog }
                />
            </UI.Field>
            <UI.Field>
                <UI.FieldLabel>Additional data</UI.FieldLabel>
                <UI.JSONInput
                    onChange={ writer("arbitraryData") }
                    value={ reader.arbitraryData ?? {} }
                />
            </UI.Field>
            { error && <ErrorMessage>{ error }</ErrorMessage> }
            { info && <InfoMessage>{ info }</InfoMessage> }
            <UI.ButtonGroup>
                <UI.Button
                    $appearance="primary"
                    disabled={ !save }
                    type="submit"
                >
                    Save
                </UI.Button>
                <UI.Button
                    type="reset"
                >
                    Reset
                </UI.Button>
            </UI.ButtonGroup>
        </UI.Form>
    );
}
